import * as React from 'react';
import { FormattedMessage, FormattedHTMLMessage } from 'react-intl';
import { produce } from 'immer';
import Dropzone from 'react-dropzone';
import {
    logger,
    addFiles,
    removeFile,
    FormFooterComponent,
    validateFieldById,
    DocUploadViewModel,
    ErrorId,
    VerificationService,
    FieldValidationErrors,
    InactiveMilitaryPersonalInfoViewModel,
} from '@sheerid/jslib-nightly';

import { AcceptableUploadsComponent as AcceptableUploads } from '../AcceptableUploadsComponent/AcceptableUploadsComponent';
import { LogoComponent } from '../LogoComponent/LogoComponent';

const MAX_DOC_UPLOAD_DOCS_ALLOWED = 3;

interface StepDocUploadProps {
    verificationService: VerificationService;
}

interface ImagePreview {
    name: string;
    url: string;
}

const StepDocUploadComponent = (props: StepDocUploadProps) => {
    const verificationService: VerificationService = props.verificationService;
    const viewModel: DocUploadViewModel = verificationService.viewModel as DocUploadViewModel;
    const fieldValidationErrors: FieldValidationErrors = verificationService.fieldValidationErrors;

    const getFileForView = (file: File) => ({
        name: file.name,
        url: file.type.includes('image') ? URL.createObjectURL(file) : '',
    });

    const currentFileNames: ImagePreview[] = [];
    if (viewModel.file1 && viewModel.file1.name) {
        currentFileNames.push(getFileForView(viewModel.file1));
    }
    if (viewModel.file2 && viewModel.file2.name) {
        currentFileNames.push(getFileForView(viewModel.file2));
    }
    if (viewModel.file3 && viewModel.file3.name) {
        currentFileNames.push(getFileForView(viewModel.file3));
    }

    const addFilesAndUpdate = (files: any[]) => {
        verificationService.updateViewModel(addFiles(viewModel, files));
    };

    const removeFileAndUpdate = (arrayIndex: number) => {
        const fileNumber = arrayIndex + 1;
        verificationService.updateViewModel(removeFile(viewModel, fileNumber));
    };

    const updateFieldValidationErrorsAndAddFiles = (files: any[]) => {
        const acceptedFiles: any[] = [];
        const errors: ErrorId[] = [];

        for (const file of files) {
            const fileValidationError: ErrorId = validateFieldById('docUpload', file) as ErrorId;

            if (fileValidationError === undefined) {
                acceptedFiles.push(file);
            } else {
                errors.push(fileValidationError);
            }
        }

        let nextFieldValidationErrors;
        if (errors) {
            nextFieldValidationErrors = produce(fieldValidationErrors, (draft) => {
                draft.docUpload = errors[0]; // we only care about one error at a time
            });
        }

        verificationService.updateFieldValidationErrors(nextFieldValidationErrors);
        addFilesAndUpdate(acceptedFiles);
    };

    const submitForm = () => {
        logger.info('StepDocUploadComponent submitting form');
        verificationService.submitStep('docUpload', viewModel, verificationService.verificationResponse);
    };

    const onFileDrop = (draggedFiles: any[]) => {
        if (draggedFiles.length > 0) {
            updateFieldValidationErrorsAndAddFiles(draggedFiles);
        } else if (draggedFiles.length <= 0) {
            logger.error('No files were accepted or rejected. Unknown error.');
        }
    };

    const fileHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            onFileDrop(Array.from(e.target.files));
        }
    };

    const personalInfo = verificationService.previousViewModel as InactiveMilitaryPersonalInfoViewModel;

    return (
        <div id="sid-step-doc-upload" className="sid-l-container">
            <div className="sid-header">
                <div className="sid-l-horz-center">
                    <LogoComponent verificationService={verificationService} />
                </div>
                <div className="sid-header__title sid-l-space-top-lg">
                    <FormattedHTMLMessage
                        id="step.docUpload.title" defaultMessage="Upload Documentation"/>
                </div>
                <p className="sid-header__subtitle">
                    <FormattedHTMLMessage
                        id="step.docUpload.subtitle" defaultMessage="To complete verification, please submit any document that clearly shows your name and your current military affiliation."/>
                </p>
                <p className="sid-l-space-btm-none sid-list__title">
                    <FormattedHTMLMessage
                        id="step.docUpload.uploadInstructions" defaultMessage="Important - Your document must display the following:"/>
                </p>

                <ul className="sid-l-space-top-none sid-must-display__list">
                    <li><FormattedHTMLMessage id="step.docUpload.includeInUpload1" defaultMessage="Your full name"/>
                    {
                        personalInfo && personalInfo.firstName && personalInfo.lastName ?
                            <span>{` (${personalInfo.firstName} ${personalInfo.lastName})`}</span> : null
                    }
                    </li>
                    <li><FormattedHTMLMessage id="step.docUpload.includeInUpload2" defaultMessage="Your branch of service"/>
                    {
                        personalInfo && personalInfo.organization && personalInfo.organization.name ?
                            <span>{` (${personalInfo.organization.name})`}</span> : null
                    }
                    </li>
                    <li><FormattedHTMLMessage id="step.docUpload.includeInUpload3" defaultMessage="Your affiliation"/>
                    {
                        personalInfo && personalInfo.status  ?
                            <span> (<FormattedHTMLMessage
                                id={`step.docUpload.${personalInfo.status}`} />)
                            </span> : null
                    }
                    </li>
                </ul>

                <p className="sid-l-space-btm-none sid-list__title">
                    <FormattedHTMLMessage id="step.docUpload.requirementsUpload" defaultMessage="Examples of acceptable documents are:"/>
                </p>
                <p className="sid-l-space-top-none">
                    <AcceptableUploads verificationService={verificationService} />
                </p>
                <p className="sid-acceptable-uploads__title">
                    <FormattedHTMLMessage id="step.docUpload.acceptableFormatsTitle" defaultMessage="Acceptable formats are: "/>
                    <span id="format-list">
                        <FormattedHTMLMessage id="step.docUpload.acceptableFormatsList" defaultMessage=".bmp, .gif, .jpg, .png, or .pdf."/>
                    </span>
                </p>
            </div>
            {
                currentFileNames.length === 0 ?
                (
                    <div>
                        <div className="sid-upload-wrap sid-l-space-top-md">
                            <div className="sid-dropzone-wrap">

                                <Dropzone
                                    onDrop={onFileDrop}
                                    className="sid-dropzone-wrap__dropzone"
                                >
                                    <p className="sid-dropzone-wrap__instructions">
                                        <FormattedHTMLMessage id="step.docUpload.fileInstructions" defaultMessage="CHOOSE FILE" />
                                    </p>
                                </Dropzone>
                                <div className="sid-custom-no-file">
                                    <FormattedHTMLMessage id="step.docUpload.noFileText" defaultMessage="No file selected"/>
                                </div>
                                {
                                    fieldValidationErrors.docUpload ? (
                                        <div className="sid-field-error">
                                            <FormattedHTMLMessage id={`errorId.${fieldValidationErrors.docUpload}`} defaultMessage="No valid files" />
                                        </div>
                                    ) : null
                                }
                            </div>
                        </div>
                    </div>
                )
                :
                null
            }

            <div className="sid-upload-wrap sid-l-space-top-sm">
                {
                    currentFileNames.length > 0 ?
                    (
                        <div className="sid-file-list">
                            {
                                currentFileNames.map((file, arrayIndex) => (
                                    <div className="sid-file-list__file" key={file.name}>
                                        {
                                            file.url ?
                                                <img className="sid-file-list__thumbnail" src={file.url} alt="File thumbnail" />
                                            :
                                            <div className="sid-file-list__no-thumbnail">
                                                <img
                                                    alt="File thumbnail"
                                                    src="https://assets-resources.sheerid.com/common/images/2018/icons/default-image.svg"
                                                />
                                            </div>
                                        }
                                        <div className="sid-file-list__name">{file.name}</div>
                                        <div
                                            role="button"
                                            tabIndex={0}
                                            className="sid-file-list__remove-btn"
                                            onClick={() => removeFileAndUpdate(arrayIndex)}
                                            onKeyPress={() => removeFileAndUpdate(arrayIndex)}
                                        />
                                    </div>
                                ))
                            }
                            {
                                currentFileNames.length < MAX_DOC_UPLOAD_DOCS_ALLOWED ?
                                (
                                    <div className="sid-add-more-files">
                                        <input
                                            type="file"
                                            id="file"
                                            className="sid-add-more-files__input"
                                            onChange={fileHandler}
                                            accept="image/jpeg,image/gif,image/png,application/pdf,image/bmp,image/gif"
                                        />
                                        <label
                                            className="sid-add-more-files__label sid-add-more-files--selectable"
                                            htmlFor="file"
                                        >
                                            <img
                                                className="sid-add-more-files__image"
                                                alt="add more files"
                                                src="https://assets-resources.sheerid.com/common/images/2018/icons/add-file.svg"
                                            />
                                            <FormattedMessage id="step.docUpload.addFile" defaultMessage="Add file" />
                                        </label>
                                    </div>
                                )
                                :
                                null
                            }
                             <div className="sid-submit sid-l-space-top-md">
                                <button
                                    onClick={submitForm}
                                    type="submit"
                                    className="sid-btn sid-btn--dark sid-submit__continue"
                                    aria-label="submit"
                                    disabled={(currentFileNames.length <= 0) ? true : false}
                                >
                                    <FormattedHTMLMessage id="step.docUpload.submitButtonLabel" defaultMessage="SUBMIT FOR REVIEW" />
                                </button>
                            </div>
                        </div>
                    )
                    :
                    null
                }
            </div>

            <div className="sid-l-space-top-md sid-h-small-text">
                <FormattedHTMLMessage id="step.docUpload.footer" defaultMessage='Please omit, cover up or black out any sensitive information such as Social Security Number, Military ID#, etc. The information we DO need to be visible: first name, last name, valid dates and/or status, for example: "retired" or "active duty".'/>
            </div>

            <div className="sid-footer sid-l-horz-center sid-l-space-top-lg sid-h-small-text">
                <FormFooterComponent verificationService={verificationService} />
            </div>
        </div>
    );
};

export { StepDocUploadComponent };
